import { supabase } from "@/lib/supabase";
import { normalizeDistrictDomain } from "@/lib/districtDomain";
import type {
  AdminAccountRow,
  LicenseInventoryRow,
  PlatformAdminRow,
} from "@/types/systemAdmin";

type RpcError = { message?: string } | null;

const mapAccountRow = (row: {
  user_id: string;
  email: string;
  user_name: string;
  workspace_persona: string | null;
  user_status: string;
  district_id: string | null;
  district_name: string | null;
  school_id: string | null;
  school_name: string | null;
  roles: string[] | null;
  is_super_admin: boolean;
  last_sign_in_at?: string | null;
  created_at: string;
}): AdminAccountRow => ({
  userId: row.user_id,
  email: row.email,
  userName: row.user_name,
  workspacePersona: row.workspace_persona,
  userStatus: row.user_status,
  districtId: row.district_id,
  districtName: row.district_name,
  schoolId: row.school_id,
  schoolName: row.school_name,
  roles: row.roles ?? [],
  isSuperAdmin: row.is_super_admin,
  lastSignInAt: row.last_sign_in_at,
  createdAt: row.created_at,
});

const mapLicenseRow = (row: {
  scope_kind: string;
  district_id: string;
  district_name: string;
  district_domain: string | null;
  school_id: string | null;
  school_name: string | null;
  leader_total: number;
  leader_used: number;
  staff_total: number;
  staff_used: number;
}): LicenseInventoryRow => ({
  scopeKind: row.scope_kind === "school" ? "school" : "district",
  districtId: row.district_id,
  districtName: row.district_name,
  districtDomain: row.district_domain,
  schoolId: row.school_id,
  schoolName: row.school_name,
  leaderTotal: row.leader_total,
  leaderUsed: row.leader_used,
  staffTotal: row.staff_total,
  staffUsed: row.staff_used,
});

export async function fetchIsSuperAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_super_admin" as never, {
    _user_id: userId,
  } as never);

  if (error) {
    console.warn("[systemAdminService] is_super_admin failed:", error.message);
    return false;
  }

  return Boolean(data);
}

export async function listAllAccounts(): Promise<AdminAccountRow[]> {
  const { data, error } = (await supabase.rpc(
    "admin_list_all_accounts" as never
  )) as { data: unknown; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to load accounts");
  return ((data ?? []) as Parameters<typeof mapAccountRow>[0][]).map(mapAccountRow);
}

export async function listLicenseInventory(): Promise<LicenseInventoryRow[]> {
  const { data, error } = (await supabase.rpc(
    "admin_list_license_inventory" as never
  )) as { data: unknown; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to load license inventory");
  return ((data ?? []) as Parameters<typeof mapLicenseRow>[0][]).map(mapLicenseRow);
}

export async function upsertOrgLicense(input: {
  districtId?: string | null;
  schoolId?: string | null;
  leaderTotal?: number;
  staffTotal?: number;
  scopeLabel?: string;
}): Promise<void> {
  const { error } = await supabase.rpc("admin_upsert_org_license" as never, {
    _district_id: input.districtId ?? null,
    _school_id: input.schoolId ?? null,
    _leader_total: input.leaderTotal ?? null,
    _staff_total: input.staffTotal ?? null,
    _scope_label: input.scopeLabel ?? null,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update license pools");
}

export async function listPlatformAdmins(): Promise<PlatformAdminRow[]> {
  const { data, error } = (await supabase.rpc(
    "admin_list_super_admins" as never
  )) as { data: unknown; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to load platform admins");

  return (
    (data ?? []) as {
      user_id: string;
      email: string;
      user_name: string;
      created_at: string;
    }[]
  ).map((row) => ({
    userId: row.user_id,
    email: row.email,
    userName: row.user_name,
    createdAt: row.created_at,
  }));
}

export async function grantPlatformAdminByEmail(email: string): Promise<string> {
  const { data, error } = (await supabase.rpc(
    "admin_grant_super_admin_by_email" as never,
    { _email: email.trim() } as never
  )) as { data: string | null; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to grant platform admin");
  return data as string;
}

export async function setPlatformAdmin(userId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.rpc("admin_set_super_admin" as never, {
    _user_id: userId,
    _enabled: enabled,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update platform admin");
}

export async function createDistrict(name: string, domain: string): Promise<string> {
  const { domain: normalizedDomain, error: domainError } = normalizeDistrictDomain(domain);

  if (!normalizedDomain) {
    throw new Error(domainError ?? "Enter a valid district email domain.");
  }

  const { data, error } = (await supabase.rpc("admin_create_district" as never, {
    _name: name.trim(),
    _domain: normalizedDomain,
  } as never)) as { data: string | null; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to create district");
  return data as string;
}

export async function createSchool(districtId: string, name: string): Promise<string> {
  const { data, error } = (await supabase.rpc("admin_create_school" as never, {
    _district_id: districtId,
    _name: name.trim(),
  } as never)) as { data: string | null; error: RpcError };

  if (error) throw new Error(error.message ?? "Failed to create school");
  return data as string;
}

export async function updateDistrict(
  districtId: string,
  name: string,
  domain: string
): Promise<void> {
  const { domain: normalizedDomain, error: domainError } = normalizeDistrictDomain(domain);

  if (!normalizedDomain) {
    throw new Error(domainError ?? "Enter a valid district email domain.");
  }

  const { error } = await supabase.rpc("admin_update_district" as never, {
    _district_id: districtId,
    _name: name.trim(),
    _domain: normalizedDomain,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update district");
}

export async function updateSchool(schoolId: string, name: string): Promise<void> {
  const { error } = await supabase.rpc("admin_update_school" as never, {
    _school_id: schoolId,
    _name: name.trim(),
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update school");
}

export async function setAccountPersona(
  userId: string,
  persona: string | null
): Promise<void> {
  const { error } = await supabase.rpc("admin_set_workspace_persona" as never, {
    _user_id: userId,
    _persona: persona,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update persona");
}

export async function setAccountAppRoles(
  userId: string,
  roles: ("teacher" | "principal" | "superintendent")[]
): Promise<void> {
  const { error } = await supabase.rpc("admin_replace_app_roles" as never, {
    _user_id: userId,
    _roles: roles,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update org role");
}

export async function setAccountStatus(
  userId: string,
  status: "active" | "pending" | "suspended" | "inactive" | "removed"
): Promise<void> {
  const { error } = await supabase.rpc("admin_update_user_status" as never, {
    _user_id: userId,
    _status: status,
  } as never);

  if (error) throw new Error(error.message ?? "Failed to update account status");
}

export async function pauseAccount(userId: string): Promise<void> {
  await setAccountStatus(userId, "suspended");
}

export async function reactivateAccount(userId: string): Promise<void> {
  await setAccountStatus(userId, "active");
}
